document.addEventListener("DOMContentLoaded", () => {
    const openModalStack = [];

    const isManagedModal = (node) => node instanceof HTMLElement
        && (node.classList.contains("v2-modal") || (node.classList.contains("modal") && node.closest(".layout-v2")));

    const resolveModalTarget = (target) => {
        if (typeof target === "string") {
            const normalizedTarget = target.startsWith("#") ? target : `#${target}`;
            return document.querySelector(normalizedTarget);
        }

        return isManagedModal(target) ? target : null;
    };

    const dispatchModalEvent = (node, eventName, relatedTarget = null) => {
        if (!(node instanceof HTMLElement)) {
            return;
        }

        const modalEvent = new CustomEvent(eventName, {
            bubbles: true,
            detail: { relatedTarget },
        });

        modalEvent.relatedTarget = relatedTarget;
        node.dispatchEvent(modalEvent);
    };

    const syncBodyScrollLock = () => {
        document.body.style.overflow = openModalStack.length ? "hidden" : "";
    };

    const showManagedModal = (target, options = {}) => {
        const node = resolveModalTarget(target);
        if (!node || openModalStack.includes(node)) {
            return;
        }

        dispatchModalEvent(node, "show.v2.modal", options.relatedTarget || null);
        dispatchModalEvent(node, "show.bs.modal", options.relatedTarget || null);

        if (node.classList.contains("v2-modal")) {
            node.hidden = false;
            node.classList.add("is-open");
            node.setAttribute("aria-hidden", "false");
        } else {
            node.style.display = "flex";
            node.classList.add("show");
            node.setAttribute("aria-hidden", "false");
        }

        openModalStack.push(node);
        syncBodyScrollLock();

        const autofocusTarget = node.querySelector("[autofocus], input, select, textarea, button");
        if (autofocusTarget instanceof HTMLElement) {
            window.requestAnimationFrame(() => autofocusTarget.focus());
        }

        dispatchModalEvent(node, "shown.v2.modal", options.relatedTarget || null);
        dispatchModalEvent(node, "shown.bs.modal", options.relatedTarget || null);
    };

    const hideManagedModal = (target, options = {}) => {
        const node = resolveModalTarget(target);
        if (!node) {
            return;
        }

        dispatchModalEvent(node, "hide.v2.modal", options.relatedTarget || null);
        dispatchModalEvent(node, "hide.bs.modal", options.relatedTarget || null);

        if (node.classList.contains("v2-modal")) {
            node.classList.remove("is-open");
            node.setAttribute("aria-hidden", "true");
            node.hidden = true;
        } else {
            node.classList.remove("show");
            node.setAttribute("aria-hidden", "true");
            node.style.display = "none";
        }

        const stackIndex = openModalStack.indexOf(node);
        if (stackIndex >= 0) {
            openModalStack.splice(stackIndex, 1);
        }

        syncBodyScrollLock();

        dispatchModalEvent(node, "hidden.v2.modal", options.relatedTarget || null);
        dispatchModalEvent(node, "hidden.bs.modal", options.relatedTarget || null);
    };

    const setFormSubmitting = (form, submitting, submitter = null) => {
        if (!(form instanceof HTMLFormElement)) {
            return false;
        }

        const submitButtons = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
        if (!submitButtons.length) {
            return false;
        }

        const activeSubmitter = submitter instanceof HTMLElement
            ? submitter
            : submitButtons.find((button) => button.dataset.submitLoadingActive === "1") || submitButtons[0];

        if (submitting) {
            if (form.dataset.submitting === "1") {
                return false;
            }

            form.dataset.submitting = "1";
            form.setAttribute("aria-busy", "true");

            submitButtons.forEach((button) => {
                button.dataset.submitWasDisabled = button.disabled ? "1" : "0";
                button.disabled = true;

                if (button === activeSubmitter) {
                    button.dataset.submitLoadingActive = "1";
                    button.classList.add("is-loading");
                    button.setAttribute("aria-disabled", "true");
                }
            });

            return true;
        }

        delete form.dataset.submitting;
        form.removeAttribute("aria-busy");

        submitButtons.forEach((button) => {
            button.disabled = button.dataset.submitWasDisabled === "1";
            button.removeAttribute("data-submit-was-disabled");

            if (button.dataset.submitLoadingActive === "1") {
                button.classList.remove("is-loading");
                button.removeAttribute("aria-disabled");
                button.removeAttribute("data-submit-loading-active");
            }
        });

        return true;
    };

    window.v2FormLoading = {
        setSubmitting: setFormSubmitting,
    };

    document.addEventListener("submit", (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        const submitter = event.submitter instanceof HTMLElement
            ? event.submitter
            : form.querySelector(".v2-auth-submit, [data-loading-submit]");

        if (!(submitter instanceof HTMLElement) || !submitter.matches(".v2-auth-submit, [data-loading-submit]")) {
            return;
        }

        if (!setFormSubmitting(form, true, submitter)) {
            event.preventDefault();
        }
    }, true);

    document.querySelectorAll("[data-fallback-src]").forEach((image) => {
        if (image.dataset.fallbackBound === "1") {
            return;
        }

        image.dataset.fallbackBound = "1";
        image.addEventListener("error", () => {
            const fallbackSrc = image.getAttribute("data-fallback-src");
            if (!fallbackSrc || image.dataset.fallbackApplied === "1") {
                return;
            }

            image.dataset.fallbackApplied = "1";
            image.src = fallbackSrc;
        });
    });

    window.V2Modal = {
        show: showManagedModal,
        hide: hideManagedModal,
    };

    window.V2AdminModal = window.V2Modal;

    document.addEventListener("click", (event) => {
        const trigger = event.target.closest('[data-modal-open], [data-toggle="modal"], [data-bs-toggle="modal"]');
        if (!trigger) {
            return;
        }

        const target = trigger.getAttribute("data-modal-open")
            || trigger.getAttribute("data-target")
            || trigger.getAttribute("data-bs-target")
            || trigger.getAttribute("href");

        if (!target || !target.startsWith("#")) {
            return;
        }

        const modal = resolveModalTarget(target);
        if (!modal) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        showManagedModal(modal, { relatedTarget: trigger });
    }, true);

    document.addEventListener("click", (event) => {
        const closeTrigger = event.target.closest("[data-modal-close], [data-dismiss='modal'], [data-bs-dismiss='modal']");
        if (!closeTrigger) {
            return;
        }

        const modal = closeTrigger.closest(".v2-modal, .modal");
        if (!isManagedModal(modal)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        hideManagedModal(modal, { relatedTarget: closeTrigger });
    }, true);

    document.addEventListener("click", (event) => {
        const modal = event.target.closest(".v2-modal, .modal");
        if (!isManagedModal(modal) || event.target !== modal) {
            return;
        }

        hideManagedModal(modal, { relatedTarget: event.target });
    });

    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") {
            return;
        }

        const activeModal = openModalStack[openModalStack.length - 1];
        if (activeModal) {
            hideManagedModal(activeModal);
        }
    });

    const modalBackdrop = document.getElementById("modals-backdrop");
    if (modalBackdrop && modalBackdrop.dataset.bound !== "1") {
        modalBackdrop.dataset.bound = "1";
        modalBackdrop.addEventListener("click", () => {
            if (typeof window.closeAllPopovers === "function") {
                window.closeAllPopovers();
            }
        });
    }

    const navigateFromCard = (card) => {
        const url = card.getAttribute("data-click-card-url");
        if (!url) {
            return;
        }

        const target = card.getAttribute("data-click-card-target");
        const rel = card.getAttribute("data-click-card-rel");

        if (target === "_blank") {
            const features = rel && rel.includes("noopener") ? "noopener,noreferrer" : undefined;
            const popup = window.open(url, "_blank", features);

            if (popup && rel && rel.includes("noopener")) {
                popup.opener = null;
            }
            return;
        }

        window.location.href = url;
    };

    document.querySelectorAll("[data-click-card-url]").forEach((card) => {
        if (card.dataset.bound === "1") {
            return;
        }

        card.dataset.bound = "1";

        card.addEventListener("click", (event) => {
            if (event.target.closest("a, button, input, select, textarea, label, [data-stop-card-nav]")) {
                return;
            }

            navigateFromCard(card);
        });

        card.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") {
                return;
            }

            if (event.target.closest("a, button, input, select, textarea, label, [data-stop-card-nav]")) {
                return;
            }

            event.preventDefault();
            navigateFromCard(card);
        });
    });
});
